import makeStyles from '@mui/styles/makeStyles';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { API } from '../../../shared/constants/constants';
import { useDispatch } from 'react-redux';
import { openSnackbar } from '../../AppSnackbar/snackbarSlice';
import { useEffect, useState } from 'react';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import ArchiveIcon from '@mui/icons-material/Archive';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import MergeIcon from '@mui/icons-material/Merge';
import MergeOrgsModal from './MergeOrgsModal';
import {
  getOrganizationsByCaseInsensitiveName,
  getOrgsByCaseInsensitiveName,
  getPersonnelByOrg,
  getPersonnelByOrganization,
} from '../contactsRequest';
import { countActiveContacts } from '../utils';
import AlertCannotArchiveOrg from '../AlertConnotArchiveOrg';
import { useArchiveContact } from '../Hooks/useArchiveContact';
import {
  normalizeOrganizationPayload,
  normalizeOrganizationQueryName,
} from '../../../shared/requests/organizationNormalization';

const ContactsPageEditOutletModal = (props) => {
  const { openModal, setOpenModal, setRefreshContacts, selectedOrg, category, customFieldConfigs } = props;
  const [textFieldValue, setTextFieldValue] = useState('');
  const [customFieldTextFieldValues, setCustomFieldTextFieldValues] = useState(
    customFieldConfigs.map((config) => ({
      fieldKey: config.fieldKey,
      label: config.label,
      value: selectedOrg[config.fieldKey] ? selectedOrg[config.fieldKey] : '',
    }))
  );
  const [openMergeOrg, setOpenMergeOrg] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const useStyles = makeStyles(() => ({
    dialog: {
      minHeight: '15vh',
      overflow: 'visible !important',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem',
    },
  }));
  const classes = useStyles();

  const archiveContactMutation = useArchiveContact({
    contactId: selectedOrg?._id,
    category: category,
    isArchiving: true,
    setRefetchContacts: setRefreshContacts,
  });

  const updateOrgMutation = useMutation({
    mutationFn: (updateData) => {
      if (category === 'Outlets') {
        return API.put(`/org/${selectedOrg._id}`, updateData);
      }
      return API.put(
        `/organization/${selectedOrg._id}`,
        normalizeOrganizationPayload(
          updateData,
          customFieldTextFieldValues.map((c) => c.fieldKey)
        )
      );
    },
    onSuccess: async (response) => {
      if (response.status !== 200) {
        alert(response.statusText);
        return;
      }
      dispatch(
        openSnackbar({
          severity: 'success',
          message: `${category.slice(0, -1)} updated!`,
        })
      );
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
      setRefreshContacts(true);
      handleClose();
    },
    onError: (err) => {
      alert(`post failed - ${err}`);
    },
  });

  const duplicatesQuery = useQuery({
    queryKey: ['orgDuplicates', category, textFieldValue, selectedOrg?._id],
    queryFn: () => {
      if (category === 'Outlets') {
        return getOrgsByCaseInsensitiveName(textFieldValue);
      }
      if (category === 'Organizations') {
        return getOrganizationsByCaseInsensitiveName(normalizeOrganizationQueryName(textFieldValue));
      }
      return Promise.resolve({ data: [] });
    },
    enabled: false,
    staleTime: 0,
  });

  const activeContactsQuery = useQuery({
    queryKey: ['orgActiveContacts', category, selectedOrg?._id],
    queryFn: () => {
      if (category === 'Outlets') {
        return getPersonnelByOrg(selectedOrg?._id);
      }
      if (category === 'Organizations') {
        return getPersonnelByOrganization(selectedOrg?._id);
      }
      return Promise.resolve({ data: [] });
    },
    enabled: false,
    staleTime: 0,
  });

  useEffect(() => {
    setTextFieldValue(selectedOrg.name);
    setCustomFieldTextFieldValues(
      customFieldConfigs.map((config) => ({
        fieldKey: config.fieldKey,
        label: config.label,
        value: selectedOrg[config.fieldKey] ? selectedOrg[config.fieldKey] : '',
      }))
    );
  }, [selectedOrg, customFieldConfigs]);

  const handleClose = () => {
    setOpenModal(false);
    setCustomFieldTextFieldValues(
      customFieldConfigs.map((config) => ({
        fieldKey: config.fieldKey,
        label: config.label,
        value: selectedOrg[config.fieldKey] ? selectedOrg[config.fieldKey] : '',
      }))
    );
  };

  const countDuplicatesButExcludeSelected = async () => {
    try {
      const { data } = await duplicatesQuery.refetch();
      const responseData = Array.isArray(data?.data) ? data.data : [];
      const filteredData = responseData.filter((outlet) => {
        return outlet._id !== selectedOrg._id;
      });
      return filteredData.length;
    } catch (error) {
      alert(`get failed - ${error}`);
      return 0;
    }
  };

  const countActiveContactsOfSelectedOrg = async () => {
    try {
      const { data } = await activeContactsQuery.refetch();
      const responseData = Array.isArray(data?.data) ? data.data : [];
      return countActiveContacts(responseData);
    } catch (error) {
      alert(`get failed - ${error}`);
      return 0;
    }
  };

  const handleArchive = async () => {
    const numActiveContactOfOrg = await countActiveContactsOfSelectedOrg();
    if (numActiveContactOfOrg !== 0) {
      setOpenAlert(true);
      return;
    }
    try {
      await archiveContactMutation.mutateAsync();
      handleClose();
    } catch (error) {
      console.log(error);
    }
  };

  const handleEdit = async (event) => {
    event.preventDefault();
    const duplicates = await countDuplicatesButExcludeSelected();

    if (duplicates > 0) {
      dispatch(
        openSnackbar({
          severity: 'warning',
          message: `${duplicates} outlet(s) with the same name already exists. Please check if you are trying to update an outlet to one that already exists.`,
        })
      );
    } else {
      const updateData = { name: textFieldValue };
      customFieldTextFieldValues.forEach((c) => {
        updateData[c.fieldKey] = c.value;
      });
      await updateOrgMutation.mutateAsync(updateData);
      event.stopPropagation();
    }
  };

  return (
    <Dialog open={openModal} onClose={handleClose} className={classes.dialog}>
      <form onSubmit={handleEdit}>
        <DialogTitle>Edit {category.slice(0, -1)}</DialogTitle>
        <DialogContent className={classes.dialog}>
          <TextField
            required
            autoFocus
            value={textFieldValue}
            onChange={(event) => {
              setTextFieldValue(event.target.value);
            }}
            margin="dense"
            id="name"
            label="Name"
            type="text"
            variant="standard"
            data-cy="name-input-ContactsPageEditOutletModal"
          />
          {customFieldTextFieldValues.map((customFieldTextFieldValue) => (
            <TextField
              key={customFieldTextFieldValue.fieldKey}
              value={customFieldTextFieldValue.value}
              id={customFieldTextFieldValue.fieldKey}
              label={customFieldTextFieldValue.label}
              data-cy={`contact-edit-outlet-modal-textfield-custom-field-${customFieldTextFieldValue.label}`}
              type="text"
              variant="standard"
              onChange={(e) => {
                setCustomFieldTextFieldValues(
                  customFieldTextFieldValues.map((c) => {
                    if (c.fieldKey === customFieldTextFieldValue.fieldKey) {
                      return {
                        ...c,
                        value: e.target.value,
                      };
                    }
                    return c;
                  })
                );
              }}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} data-cy="cancel-button-ContactsPageEditOutletModal">
            Cancel
          </Button>
          <Button
            onClick={() => setOpenMergeOrg(true)}
            startIcon={<MergeIcon />}
            data-cy="merge-button-ContactsPageEditOutletModal"
          >
            Merge
          </Button>
          <Button
            onClick={() => handleArchive()}
            startIcon={<ArchiveIcon />}
            data-cy="archive-button-ContactsPageEditOutletModal"
          >
            Archive
          </Button>
          <Button type="submit" startIcon={<SyncAltIcon />} data-cy="update-button-ContactsPageEditOutletModal">
            Update
          </Button>
        </DialogActions>
      </form>

      <MergeOrgsModal
        openMergeOrg={openMergeOrg}
        setOpenMergeOrg={setOpenMergeOrg}
        selectedOrg={selectedOrg}
        setRefreshContacts={setRefreshContacts}
        setOpenEditOrg={setOpenModal}
        category={category}
      />

      <AlertCannotArchiveOrg
        open={openAlert}
        setOpen={setOpenAlert}
        isOrganizationCategory={category.slice(0, -1) === 'Organization'}
      />
    </Dialog>
  );
};

export default ContactsPageEditOutletModal;
